// Agency: Onboarding — ch.06: "Intake → connections → rules → internal test →
// customer test → release … Milestone records derived from completed checks and
// evidence. Milestone display never grants execution permission."
//
// One `GET /onboarding` per office in the portfolio. The endpoint derives every
// milestone from the records that own the fact (apps/api/routes/onboarding.ts);
// this screen renders what it said and adds nothing.
//
// ---------------------------------------------------------------------------
// The milestone is a record, not a tick
// ---------------------------------------------------------------------------
//
// The demo draws six steps with a line through the finished ones. That is the
// display ch.06's second sentence is about: a step that reads as done invites
// somebody to conclude the thing behind it is switched on, and in this system
// it never is — nothing consults a release, so a released scope still waits for
// a person.
//
// So a milestone here is shown as EVIDENCED and names its evidence, or as not
// recorded. There is no tick, no strike-through and no percentage. The one
// place a stronger word would be justified is the release, and that is exactly
// the place it would be read as permission, so the release row carries the
// sentence saying nothing acts on it.
//
// ---------------------------------------------------------------------------
// The third state, and why it is not a fourth kind of empty
// ---------------------------------------------------------------------------
//
// `not_collected` means nothing in this system records this at all. ch.06 lists
// a customer test between the internal test and the release and there is no
// customer test run, dataset or gate anywhere — so that row says so and stays
// saying so however much work an office does. It renders differently from
// `absent`, which is a step somebody can still take.

import { useCallback, useState, type ReactNode } from 'react';
import { request, tenantPath } from '../api/client.ts';
import type { AdministrationOnboarding, Membership, OnboardingMilestone } from '../api/types.ts';
import { agencyMemberships, useOfficeReads } from '../agency.ts';
import { useWorkspace } from '../state/workspace.tsx';
import * as format from '../format.ts';

/**
 * How each milestone key reads, in ch.06's order.
 *
 * The keys stay canonical on the wire — contracts/http-api.md: "Localise
 * presentation without changing canonical enum values" — and this is only their
 * presentation. A key this table does not know renders as itself rather than as
 * a word invented here.
 */
const MILESTONE_LABELS: Readonly<Record<string, string>> = {
  intake: 'Intake',
  connections: 'Connections',
  rules: 'Working rules',
  internal_test: 'Internal test',
  customer_test: 'Customer test',
  office_approval: 'Office approval',
  release: 'Release',
};

function label(key: string): string {
  return MILESTONE_LABELS[key] ?? key.replace(/_/g, ' ');
}

/** The badge for each state. Three states, three appearances, no ticks. */
function badge(state: OnboardingMilestone['state']): { className: string; text: string } {
  switch (state) {
    case 'evidenced':
      return { className: 'badge ok', text: 'Recorded' };
    case 'absent':
      return { className: 'badge', text: 'Nothing recorded' };
    default:
      // Visibly different from `absent`: an office cannot reach this state by
      // doing more work, because nothing would write it down.
      return { className: 'badge warn', text: 'Not collected' };
  }
}

export function AgencyOnboardingScreen(): ReactNode {
  const workspace = useWorkspace();
  const { token } = workspace;
  const offices = agencyMemberships(workspace.me);
  const [expanded, setExpanded] = useState<string | null>(null);

  const read = useCallback(
    async (membership: Membership): Promise<readonly AdministrationOnboarding[]> => {
      const body = await request<{ administrations: AdministrationOnboarding[] }>(
        tenantPath(membership.tenantId, '/onboarding'),
        { token },
      );
      return body.administrations;
    },
    [token],
  );

  const { results, busy, reload } = useOfficeReads(offices, read);

  if (offices.length === 0) {
    return (
      <>
        <div className="heading">
          <h1>Onboarding</h1>
          <p>This account operates no office.</p>
        </div>
        <div className="card">
          <div className="empty">
            <h3>No portfolio</h3>
            <p>Onboarding is shown for the offices where you hold an agency operator membership.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="heading">
        <h1>Onboarding</h1>
        <p>
          What has been recorded for each administration you were granted, read from that office's
          own records. A recorded milestone is evidence that something happened — it is not a
          statement that a step is finished, and it switches nothing on.
        </p>
      </div>

      <div className="toolbar">
        <p className="muted small">
          {offices.length === 1
            ? 'One office in your portfolio.'
            : `${String(offices.length)} offices in your portfolio.`}
        </p>
        <button type="button" className="btn" disabled={busy} onClick={reload}>
          {busy ? 'Reading…' : 'Refresh'}
        </button>
      </div>

      {results === null ? (
        <div className="card">
          <div className="empty">
            <h3>Reading your offices</h3>
            <p>One request per office, in turn.</p>
          </div>
        </div>
      ) : (
        results.map((entry) => (
          <div
            key={entry.membership.tenantId}
            className="card section-space"
            data-testid={`onboarding-office-${entry.membership.tenantId}`}
          >
            <div className="card-head">
              <div>
                <h2>{entry.membership.tenantName}</h2>
                <p>
                  {entry.value === null
                    ? 'Not read'
                    : entry.value.length === 0
                      ? 'No administration in this office is granted to you'
                      : `${String(entry.value.length)} administration${entry.value.length === 1 ? '' : 's'} granted to you`}
                </p>
              </div>
            </div>

            {entry.value === null ? (
              <div className="card-body">
                {/* Never an empty milestone list: an office that could not be
                    read would otherwise look like one with no records. */}
                <p className="callout warn">{entry.error}</p>
              </div>
            ) : entry.value.length === 0 ? (
              <div className="empty">
                <h3>Nothing granted here</h3>
                <p>
                  ch.02: agency support has no standing unrestricted access. This office has not
                  granted you an administration, so there is nothing of its onboarding to show.
                </p>
              </div>
            ) : (
              entry.value.map((administration) => (
                <AdministrationBlock
                  key={administration.administrationId}
                  administration={administration}
                  expanded={expanded === administration.administrationId}
                  onToggle={() =>
                    setExpanded((current) =>
                      current === administration.administrationId
                        ? null
                        : administration.administrationId,
                    )
                  }
                />
              ))
            )}
          </div>
        ))
      )}

      <div className="card section-space">
        <div className="card-head">
          <h2>How to read this</h2>
        </div>
        <div className="card-body">
          <ul className="note-list">
            <li>
              <b>Recorded is not done.</b> Every row names the records behind it. "Three policy
              versions requested" is a fact; whether that is far enough along is a judgement
              somebody makes, and this screen does not make it for them.
            </li>
            <li>
              <b>Nothing here grants permission.</b> ch.06 says milestone display never does, and
              in this build it could not: nothing consults a release, so a released scope still
              waits for a person.
            </li>
            <li>
              <b>Customer test is not collected.</b> ch.06 names the step and this system has no
              customer test run, dataset or gate. The office's written approval is shown separately
              because a signature is not a test.
            </li>
            <li>
              <b>Only your grants.</b> An office may hold administrations you were not let into,
              and those are absent here rather than refused.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function AdministrationBlock({
  administration,
  expanded,
  onToggle,
}: {
  readonly administration: AdministrationOnboarding;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}): ReactNode {
  const recorded = administration.milestones.filter((entry) => entry.state === 'evidenced').length;
  const collectible = administration.milestones.filter(
    (entry) => entry.state !== 'not_collected',
  ).length;

  return (
    <div className="row" data-testid={`onboarding-${administration.administrationId}`}>
      <div className="grow">
        <strong>{administration.name}</strong>
        <p>
          {/* A COUNT, not a percentage, and the denominator excludes the step
              nothing collects — so the figure is "how many of the things that
              can be recorded have been", which is checkable. */}
          {recorded} of {collectible} milestones recorded
          {administration.paused ? ' · paused' : ''}
        </p>

        <div className="flex" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {administration.milestones.map((entry) => {
            const appearance = badge(entry.state);
            return (
              <span
                key={entry.key}
                className={appearance.className}
                data-testid={`milestone-${administration.administrationId}-${entry.key}`}
                title={entry.detail}
              >
                {label(entry.key)}: {appearance.text}
              </span>
            );
          })}
        </div>

        {expanded ? (
          <div className="trace" style={{ marginTop: 12 }}>
            {administration.milestones.map((entry) => (
              <p key={entry.key} data-testid={`detail-${administration.administrationId}-${entry.key}`}>
                <b>{label(entry.key)}.</b> {entry.detail}
                {entry.at === null ? '' : ` (${format.day(entry.at)})`}
              </p>
            ))}
          </div>
        ) : null}
      </div>
      <button type="button" className="btn" onClick={onToggle}>
        {expanded ? 'Hide evidence' : 'Show evidence'}
      </button>
    </div>
  );
}
